import type { ComponentType, ComponentProps, ReactNode } from 'react'
import { Suspense, lazy } from 'react'

import ErrorBoundary from '@space-shell/common/components/errorBoundary'
import LoaderFull from '@space-shell/common/components/loaderFull'

/**
 * A higher-order component (HOC) that wraps a lazy-loaded component with an
 * `ErrorBoundary` and `Suspense` for handling errors and loading states.
 *
 * This HOC is designed to work with React's `lazy` and `Suspense` to lazily
 * load a component, handle errors via an error boundary, and display a fallback
 * while the component is loading.
 *
 * @param factory - A factory function that returns a promise resolving to a
 *                  default-exported component. This function is used by React's
 *                  `lazy` to load the component.
 *
 * @param fallback - A `ReactNode` to be displayed while the lazy-loaded component
 *                   is being fetched. Defaults to the `LoaderFull` component if not
 *                   provided.
 *
 * @returns A React component that is wrapped with the `ErrorBoundary` and `Suspense`
 *          components, ensuring that errors are caught and a fallback is shown during
 *          lazy loading.
 *
 * @example
 * ```tsx
 * const Home = lazyWithErrorBoundary(() => import('@space-shell/pages/home/server'));
 *
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lazyWithErrorBoundary = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  fallback: ReactNode = <LoaderFull />
): ComponentType<ComponentProps<T>> => {
  const LazyComponent = lazy(factory)

  return function WrappedComponent(props: ComponentProps<T>) {
    return (
      <ErrorBoundary>
        <Suspense fallback={fallback}>
          <LazyComponent {...props} />
        </Suspense>
      </ErrorBoundary>
    )
  }
}

export default lazyWithErrorBoundary
